import { Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMiranda } from '@/contexts/MirandaContext';
import { cn } from '@/lib/utils';
import { isPublicRoute } from '@/lib/constants/public-routes';
import { useLocation } from 'react-router-dom';
import { useSyncExternalStore } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { canShowMirandaFab, shouldHideMirandaFab } from './fab-visibility';
import { useAuth } from '@/contexts/AuthContext';
import { getAccessToken } from '@/services/api';

const LG_BREAKPOINT = 1024;

/**
 * F4 (runbook 7) — Amb un full o un diàleg **modal** obert, el botó (`fixed bottom-6 right-6
 * z-[60]`) tapava els seus botons (p. ex. «Crear a Beds24» a 385 px). Sense estat global: es llegeix
 * el que ja exposa Radix. Un contingut obert és `[role="dialog"|"alertdialog"][data-state="open"]`,
 * i només en mode modal Radix posa `pointer-events: none` al `body` (`disableOutsidePointerEvents`).
 * Cal tot dos: la fitxa de reserva i el Miranda costat a costat són `modal={false}` i el botó
 * s'hi ha de quedar (F2); un menú desplegable modal no és `role="dialog"`.
 */
const OPEN_DIALOG_SELECTOR = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';

interface ModalProbeDocument {
  body: { style: { pointerEvents: string } } | null;
  querySelector: (selector: string) => unknown;
}

export function isModalLayerOpen(doc: ModalProbeDocument): boolean {
  if (!doc.body || doc.body.style.pointerEvents !== 'none') return false;
  return doc.querySelector(OPEN_DIALOG_SELECTOR) !== null;
}

function subscribeToModalLayers(onChange: () => void): () => void {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return () => {};
  const observer = new MutationObserver(onChange);
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['data-state', 'style'],
  });
  return () => observer.disconnect();
}

function getModalLayerSnapshot(): boolean {
  return typeof document !== 'undefined' && isModalLayerOpen(document);
}

function useModalLayerOpen(): boolean {
  return useSyncExternalStore(subscribeToModalLayers, getModalLayerSnapshot, () => false);
}

interface MirandaFABProps {
  className?: string;
}

/**
 * Floating Action Button for Miranda AI Assistant
 * Appears on all pages when MirandaProvider is active
 * Only visible for authenticated users (staff/admin)
 */
export function MirandaFAB({ className }: MirandaFABProps) {
  const { toggle, isOpen, isStreaming, routeFocus } = useMiranda();
  // D7: `useLocation` (i no `window.location`) perquè el botó es torni a pintar en navegar.
  const { pathname } = useLocation();
  const belowLg = useIsMobile(LG_BREAKPOINT);
  // Es deriva del DOM a cada canvi: en tancar-se el diàleg (inclòs el full de Miranda en mode
  // modal, que ja amaga el botó amb `isOpen`) el botó torna sol.
  const modalOpen = useModalLayerOpen();

  // Only show for authenticated users (there is a stored access token).
  // Also hide on public routes — see `lib/constants/public-routes.ts` for the
  // canonical list and rationale. A guest with a stale token still present on
  // the device must NOT see Miranda on a public guest form.
  // And only with a verified user: a stored token alone also happens on the «No s'ha pogut
  // connectar» screen, where the button must not float over it.
  const { isAuthenticated } = useAuth();
  const hasToken = typeof window !== 'undefined' && !!getAccessToken();
  const onPublicRoute = typeof window !== 'undefined' && isPublicRoute(window.location.pathname);

  if (
    !canShowMirandaFab({ isAuthenticated, hasToken, onPublicRoute }) ||
    modalOpen ||
    shouldHideMirandaFab({ pathname, belowLg })
  ) {
    return null;
  }

  // 🆕 F2 — Amb una fitxa de reserva oberta, el drawer tapava aquest botó i no es podia ni
  // obrir el xat sense tancar la fitxa (informe de PROD, research §0.2: tots dos a `z-50` i
  // la fitxa es porta al final del DOM). Ara el botó puja per sobre i, a partir de `sm`, se
  // situa a l'esquerra de la fitxa (`max-w-md` = 28rem). Per sota de `sm` la fitxa ocupa tota
  // l'amplada i no hi ha lloc: allà s'hi entra pel botó "Miranda" de la seva capçalera.
  const behindSheet = routeFocus !== null;

  return (
    <Button
      onClick={toggle}
      size="lg"
      // La fitxa és `modal={false}`: sense això, el `pointerdown` d'aquest botó la tancaria
      // (i navegaria a `/calendar-v2`) abans que el `click` arribés a obrir Miranda.
      data-sheet-outside-safe
      className={cn(
        'fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full shadow-lg',
        'bg-primary hover:bg-primary/90',
        'transition-all duration-200',
        // 29,5rem = l'amplada de la fitxa (`sm:max-w-md`, 28rem) + el marge de 1,5rem.
        behindSheet && 'hidden sm:inline-flex sm:right-[29.5rem]',
        isOpen && 'scale-0 opacity-0',
        isStreaming && 'animate-pulse',
        className
      )}
      aria-label={isOpen ? 'Tancar Miranda' : 'Obrir Miranda'}
    >
      <Bot className="h-6 w-6" />
    </Button>
  );
}

export default MirandaFAB;
